import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Book, BackgroundJob } from '../../types';
import {
  getGraphQLClient,
  GET_BOOK,
  CREATE_BOOK,
  GENERATE_COVER_IMAGE_ASYNC,
} from '../../lib/graphql';
import { useBackgroundJobsContext } from '../../hooks/useBackgroundJobs';
import WizardStepIndicator from './WizardStepIndicator';
import BookBasicsStep from './BookBasicsStep';
import DescriptionStep from './DescriptionStep';
import CoverStep from './CoverStep';

interface BookWizardProps {
  onComplete: (bookId: string) => void;
  onCancel: () => void;
  initialStep?: number;
  bookId?: string;
  onStepChange?: (step: number, bookId?: string) => void;
  isEditMode?: boolean;
}

export default function BookWizard({
  onComplete,
  onCancel,
  initialStep = 1,
  bookId,
  onStepChange,
  isEditMode = false,
}: BookWizardProps) {
  const { t, i18n } = useTranslation();
  const { trackJob } = useBackgroundJobsContext();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const bookCreationAttempted = useRef(false);

  // Sync currentStep with initialStep when URL changes
  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  // Fetch book data when bookId is provided, or create a new book if not
  useEffect(() => {
    const client = getGraphQLClient();

    if (bookId) {
      // Fetch existing book
      setLoading(true);
      setError(null);
      client
        .request<{ book: Book }>(GET_BOOK, { id: bookId })
        .then((data) => {
          if (data.book) {
            setBook(data.book);
          } else {
            setError('Book not found');
          }
        })
        .catch((err: Error) => {
          setError(err.message || 'Failed to load book');
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (!bookCreationAttempted.current) {
      // Create a new book immediately with minimal data
      bookCreationAttempted.current = true;
      setLoading(true);
      setError(null);
      client
        .request<{ createBook: { book: Book; errors: string[] } }>(CREATE_BOOK, {
          language: i18n.language || 'en',
        })
        .then((data) => {
          if (data.createBook.errors.length > 0) {
            setError(data.createBook.errors[0]);
          } else if (data.createBook.book) {
            setBook(data.createBook.book);
            // Navigate to the URL with the new book ID
            if (onStepChange) {
              onStepChange(1, data.createBook.book.id);
            }
          }
        })
        .catch((err: Error) => {
          setError(err.message || 'Failed to create book');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [bookId, i18n.language, onStepChange]);

  const navigateToStep = (step: number, newBookId?: string) => {
    setCurrentStep(step);
    if (onStepChange) {
      onStepChange(step, newBookId);
    }
  };

  const handleStepClick = (step: number) => {
    // Only allow navigation to previous steps
    if (step < currentStep && book) {
      navigateToStep(step, book.id);
    }
  };

  const handleBookCreated = (createdBook: Book) => {
    setBook(createdBook);
    navigateToStep(2, createdBook.id);
  };

  const handleBookChange = (updatedBook: Book) => {
    setBook(updatedBook);
  };

  const handleDescriptionNext = async () => {
    if (book) {
      if (isEditMode) {
        // For edit mode, go to cover step
        navigateToStep(3, book.id);
      } else {
        // For create mode, this is the final step - trigger cover generation and complete
        if (!book.coverImageUrl) {
          try {
            const client = getGraphQLClient();
            const data = await client.request<{
              generateCoverImage: {
                coverImageUrl: string | null;
                backgroundJob?: BackgroundJob;
                errors: string[];
              };
            }>(GENERATE_COVER_IMAGE_ASYNC, {
              bookId: book.id,
            });

            if (data.generateCoverImage.backgroundJob) {
              trackJob(data.generateCoverImage.backgroundJob);
            }
          } catch {
            // Silently fail - cover generation is not critical
            console.error('Failed to start cover generation');
          }
        }

        onComplete(book.id);
      }
    }
  };

  const handleCoverNext = () => {
    // Cover is the last step for book editing - complete the wizard
    if (book) {
      onComplete(book.id);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      navigateToStep(currentStep - 1, book?.id);
    }
  };

  // Show loading state when fetching book data
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="card p-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">{t('common.loading')}</span>
          </div>
        </div>
      </motion.div>
    );
  }

  // Show error state if book fetch failed
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="card p-8">
          <div className="text-center py-8">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button onClick={onCancel} className="btn-secondary">
              {t('common.back')}
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="card p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
          {isEditMode ? t('books.edit') : t('books.create')}
        </h1>

        <WizardStepIndicator
          currentStep={currentStep}
          totalSteps={isEditMode ? 3 : 2}
          onStepClick={handleStepClick}
        />

        <AnimatePresence mode="wait">
          {currentStep === 1 && book && (
            <BookBasicsStep
              key="step1"
              book={book}
              onComplete={handleBookCreated}
              onCancel={onCancel}
            />
          )}

          {currentStep === 2 && book && (
            <DescriptionStep
              key="step2"
              book={book}
              onBookChange={handleBookChange}
              onNext={handleDescriptionNext}
              onBack={handleBack}
              isEditMode={isEditMode}
            />
          )}

          {currentStep === 3 && book && isEditMode && (
            <CoverStep
              key="step3-cover"
              book={book}
              onBookChange={handleBookChange}
              onNext={handleCoverNext}
              onBack={handleBack}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
